import styles from './CashflowGrid.module.css'
import {
  useEffect,
  useMemo,
  useCallback,
  Dispatch,
  SetStateAction,
} from 'react'
import { AgGridReact } from 'ag-grid-react'
import 'ag-grid-enterprise'
import 'ag-grid-community/styles/ag-grid.css'
import 'ag-grid-community/styles/ag-theme-balham.css'
import { CellClassParams, ColDef } from 'ag-grid-community'
import { defaultColDef } from '../gridConfig/DefaultColDef'
import { AG_GRID_LOCALE_SV } from '../../../MainGrids/gridConfig/SwedishLocale'
import TotalTextCellRenderer from '../cellRenderers/TotalTextCellRenderer'
import LoadingSpinner from '../../../StatusIndicators/LoadingSpinner'
import { customAggFuncs } from '../helper/customAggFuncs'
import { LicenseManager } from 'ag-grid-enterprise'
import { useProjektprognosEditingContext } from '../../context/ProjektprognosEditingContext'
import { useProjektprognosInteractionContext } from '../../context/ProjektprognosInteractionContext'
import { useProjektprognosSheetContext } from '../../context/ProjektprognosSheetContext'
import { useCashflowReportData } from '../../fetchData/queries/cashflow/useCashflowReportData'
import { useProjektprognosCashflowNotices } from '../../fetchData/queries/cashflow/useCashflowNotices'
import { UnableToLoadData } from '../../../SystemState/ErrorManagement/UnableToLoadData/UnableToLoadData'
import { useCashflowColDefs } from './useProjektprognosCashflowColDefs'
import { useStatusBar } from '../customHooks/useStatusBar'
import { useGetRowId } from '../customHooks/useGetRowId'
import { useOnCellDoubleClicked } from '../customHooks/useOnCellDoubleClicked'
import { useOnCellContextMenu } from '../customHooks/useOnCellContextMenu'
import { useGetContextMenuItems } from '../customHooks/useGetContextMenuItems'
import { createGetRowClassCallback } from '../helper/filterFunctions'
import { gridLicenseKey } from '../gridLicenseKey'
import { extractAccountsFromData } from '../AccountFilter/utils/extractAccounts'
import { useExternalAccountFilter } from '../AccountFilter/hooks/useExternalAccountFilter'
import { applyCommentsToGridRows } from '../../fetchData/helpers/applyCommentsToGridRows'
import { useCommentUpdater } from '../customHooks/useCommentUpdater'

LicenseManager.setLicenseKey(gridLicenseKey)

interface CashflowGridProps {
  setIsAccountSpecificationModalOpen: Dispatch<SetStateAction<boolean>>
  modalAccountId: string
  setModalAccountId: Dispatch<SetStateAction<string>>
  modalOutcomeDate: string
  setModalOutcomeDate: Dispatch<SetStateAction<string>>
}

const CashflowGrid = ({
  setIsAccountSpecificationModalOpen,
  modalAccountId,
  setModalAccountId,
  modalOutcomeDate,
  setModalOutcomeDate,
}: CashflowGridProps) => {
  const {
    rows: cashflowData,
    previousSnapshotName,
    isLoading: isReportLoading,
    error: cashflowError,
  } = useCashflowReportData()
  const accountNotices = useProjektprognosCashflowNotices().data

  const { loadProjektprognos } = useProjektprognosInteractionContext()

  const { emptyRowsHiddenToggle, selectedPeriodReport } =
    useProjektprognosEditingContext()

  const {
    cashflowGridRef,
    cashflowRows,
    setCashflowRows,
    lockedBudget,
    datesInfo,
    setMonthColumnIds,
    selectedCashflowAccountIds,
    filterActive,
    minDiff,
    enableDiffFilter,
    enableCommentFilter,
  } = useProjektprognosSheetContext()

  // Show loading overlay when report data is loading
  useEffect(() => {
    if (cashflowGridRef.current?.api) {
      if (isReportLoading) {
        cashflowGridRef.current.api.showLoadingOverlay()
      } else {
        cashflowGridRef.current.api.hideOverlay()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReportLoading])

  const effectiveEnableDiffFilter = enableDiffFilter && !isReportLoading

  // Extract unique accounts for filtering
  const accounts = useMemo(() => {
    return extractAccountsFromData(cashflowRows?.prefixedData)
  }, [cashflowRows?.prefixedData])

  const allAccountIds = useMemo<Set<number>>(() => {
    return new Set(accounts.map((a) => a.accountId))
  }, [accounts])

  // Use external filter system to show group rows even when all children are filtered out
  const { isExternalFilterPresent, doesExternalFilterPass } =
    useExternalAccountFilter(
      selectedCashflowAccountIds,
      allAccountIds,
      filterActive,
      accounts,
      effectiveEnableDiffFilter,
      minDiff,
      enableCommentFilter
    )

  useEffect(() => {
    if (cashflowData) setCashflowRows(cashflowData)
  }, [cashflowData, loadProjektprognos, setCashflowRows])

  // Handle comments: clear old comments and apply period report comments when available
  useEffect(() => {
    if (!cashflowData) return

    // Clear all old comments from rows (from previous comment system)
    const clearComments = (rows: any) => {
      return {
        ...rows,
        prefixedData: rows.prefixedData.map((row: any) => ({
          ...row,
          comment: undefined,
        })),
        emptyRowsHidden: rows.emptyRowsHidden.map((row: any) => ({
          ...row,
          comment: undefined,
        })),
      }
    }

    // If period report is selected and has comments, apply them
    if (
      selectedPeriodReport &&
      selectedPeriodReport.comments?.accountComments &&
      selectedPeriodReport.comments.accountComments.length > 0
    ) {
      const clearedRows = clearComments(cashflowData)
      const updatedRows = applyCommentsToGridRows(
        clearedRows,
        selectedPeriodReport.comments.accountComments
      )
      if (updatedRows) {
        setCashflowRows(updatedRows)
      }
    } else {
      // No period report selected - clear all comments
      const clearedRows = clearComments(cashflowData)
      setCashflowRows(clearedRows)
    }
  }, [selectedPeriodReport, cashflowData, setCashflowRows])

  // Update AG Grid filter when filterActive or selectedAccountIds change
  useEffect(() => {
    if (cashflowGridRef.current?.api) {
      cashflowGridRef.current.api.onFilterChanged()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filterActive,
    selectedCashflowAccountIds,
    effectiveEnableDiffFilter,
    enableCommentFilter,
    minDiff,
    doesExternalFilterPass,
  ])

  const gridStyle = useMemo(() => ({ height: '100%', width: '100%' }), [])

  const cashflowColDefsHook = {
    datesInfo,
    lockedBudget,
    setMonthColumnIds,
    selectedSnapshotName: previousSnapshotName,
  }

  const cashflowColDefs = useCashflowColDefs({ ...cashflowColDefsHook })
  const getRowId = useGetRowId()
  const statusBar = useStatusBar()

  // Check if period report is active and not closed (editable)
  const isPeriodReportActive = !!selectedPeriodReport
  const isPeriodReportEditable =
    isPeriodReportActive && selectedPeriodReport?.status !== 'Closed'

  const { updateComment } = useCommentUpdater({
    selectedPeriodReportId: selectedPeriodReport?.id || null,
    isPeriodReportEditable,
  })

  // Combined cell value changed handler
  const handleCellValueChanged = useCallback(
    (event: any) => {
      // Handle comments via period report when active
      if (isPeriodReportActive && event.colDef.field === 'comment') {
        const accountId = event.data?.accountId
        if (accountId) {
          updateComment(accountId, event.newValue || '')
        }
      }
    },
    [isPeriodReportActive, updateComment]
  )
  const onCellDoubleClicked = useOnCellDoubleClicked(cashflowGridRef)
  const onCellContextMenu = useOnCellContextMenu(
    modalAccountId,
    modalOutcomeDate
  )

  const getContextMenuItemsHookParams = {
    totals: undefined,
    setIsAccountSpecificationModalOpen,
    setModalAccountId,
    setModalOutcomeDate,
  }

  const getContextMenuItems = useGetContextMenuItems({
    ...getContextMenuItemsHookParams,
  })

  const autoGroupColumnDef = useMemo((): ColDef => {
    return {
      headerName: 'Kassaflöde',
      cellRendererParams: {
        suppressCount: true,
        suppressDoubleClickExpand: true,
        innerRenderer: TotalTextCellRenderer,
        innerRendererParams: { ...accountNotices },
      },
      field: 'displayName',
      cellStyle: (params: CellClassParams) => {
        const colStyle = { backgroundColor: '#d9dcde', fontWeight: '' }

        switch (params.node.level) {
          case 1:
            colStyle.backgroundColor = `${colStyle.backgroundColor}30`
            colStyle.fontWeight = '700'
            break
          case 2:
            colStyle.backgroundColor = params.node.data?.editable
              ? 'transparent'
              : `${colStyle.backgroundColor}30`
            break
          default:
            colStyle.backgroundColor = `${colStyle.backgroundColor}80`
            colStyle.fontWeight = '900'
            break
        }
        return colStyle
      },
    }
  }, [accountNotices])

  const onFirstDataRendered = useCallback(() => {
    if (cashflowGridRef.current?.api && cashflowGridRef.current?.columnApi) {
      // Wait a bit for all groups to expand before measuring
      setTimeout(() => {
        if (cashflowGridRef.current?.columnApi) {
          cashflowGridRef.current.columnApi.autoSizeColumns([
            'ag-Grid-AutoColumn',
          ])
        }
      }, 100)
    }
  }, [cashflowGridRef])

  const onGridSizeChanged = useCallback(() => {
    if (cashflowGridRef.current?.columnApi) {
      cashflowGridRef.current.columnApi.autoSizeColumns(['ag-Grid-AutoColumn'])
    }
  }, [cashflowGridRef])

  // Auto-size column when groups are opened/closed
  const onRowGroupOpened = useCallback(() => {
    if (cashflowGridRef.current?.columnApi) {
      setTimeout(() => {
        if (cashflowGridRef.current?.columnApi) {
          cashflowGridRef.current.columnApi.autoSizeColumns([
            'ag-Grid-AutoColumn',
          ])
        }
      }, 50)
    }
  }, [cashflowGridRef])

  // Mark selected rows with focusAccount CSS class and accounts with high diff with red background
  const getRowClass = useMemo(
    () =>
      createGetRowClassCallback(
        selectedCashflowAccountIds,
        accounts,
        minDiff,
        enableCommentFilter
      ),
    [selectedCashflowAccountIds, accounts, minDiff, enableCommentFilter]
  )

  return (
    <div
      className="ag-theme-balham"
      style={gridStyle}
      data-testid="CashflowGrid"
    >
      {cashflowError ? (
        <div className={styles.topBorderOnError}>
          <UnableToLoadData message={'Kassaflöde kunde ej laddas'} />
        </div>
      ) : (
        <AgGridReact
          ref={cashflowGridRef}
          rowData={
            emptyRowsHiddenToggle
              ? cashflowRows?.emptyRowsHidden
              : cashflowRows?.prefixedData
          }
          columnDefs={cashflowColDefs}
          rowSelection={'multiple'}
          defaultColDef={defaultColDef}
          autoGroupColumnDef={autoGroupColumnDef}
          getContextMenuItems={getContextMenuItems}
          onCellContextMenu={onCellContextMenu}
          onCellDoubleClicked={onCellDoubleClicked}
          onCellValueChanged={handleCellValueChanged}
          onFirstDataRendered={onFirstDataRendered}
          onGridSizeChanged={onGridSizeChanged}
          onRowGroupOpened={onRowGroupOpened}
          groupDefaultExpanded={2}
          statusBar={statusBar}
          enableRangeSelection={true}
          enableRangeHandle={false}
          localeText={AG_GRID_LOCALE_SV}
          loadingOverlayComponent={LoadingSpinner}
          suppressAggFuncInHeader={true}
          getRowId={getRowId}
          groupRowsSticky={true}
          aggFuncs={customAggFuncs}
          groupAllowUnbalanced
          groupMaintainOrder
          suppressAggAtRootLevel={true}
          isExternalFilterPresent={isExternalFilterPresent}
          doesExternalFilterPass={doesExternalFilterPass}
          suppressAggFilteredOnly={true}
          suppressRowHoverHighlight={true}
          getRowClass={getRowClass}
        />
      )}
    </div>
  )
}

export default CashflowGrid
